import {
  type NamedHierarchyNode,
  toFolderNode,
  type VisibleContract,
} from "@contract-manager/shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { ApiClient } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { describeError, isSignedOutError } from "../auth/authErrors";
import { useCurrentUser } from "../auth/CurrentUser";
import { Alert } from "../components/Alert";
import {
  HierarchyAdmin,
  type NodeOperations,
} from "../components/HierarchyAdmin";
import { PermissionEditor } from "../components/PermissionEditor";
import { StatusBadge } from "../components/StatusBadge";
import { useI18n } from "../i18n/I18n";

/**
 * Folder管理: the tree on the left, the contracts filed in the selected
 * folder on the right.
 *
 * Contracts are filed from the contract's own page rather than dragged in
 * here — the decision "which folder does this belong in" is made while looking
 * at the contract, not while looking at the folder.
 */
export function FolderAdminPage({ api }: { api: ApiClient }) {
  const { isAdmin } = useCurrentUser();
  const { t } = useI18n();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Depends on `api` alone: anything `list` itself updates must not be in
  // here, or every load would build new operations and trigger the next load.
  const operations = useMemo<NodeOperations>(
    () => ({
      kind: "folder",
      list: async () => (await api.listFolders()).items.map(toFolderNode),
      create: (input) => api.createFolder(input),
      update: (id, changes) => api.updateFolder(id, changes),
      remove: (id) => api.deleteFolder(id),
    }),
    [api],
  );

  return (
    <div className="ds-rows --gap-medium">
      <div className="ds-cols --baseline --gap-small">
        <h1>{t("organisation.folders.title")}</h1>
        <Link to="/" className="ds-button --plain --small">
          <span>{t("common.backToList")}</span>
        </Link>
      </div>

      <HierarchyAdmin
        operations={operations}
        canManage={isAdmin}
        selectedId={selectedId}
        onSelectedChange={setSelectedId}
      >
        {(folder) => <FolderContents api={api} folder={folder} />}
      </HierarchyAdmin>
    </div>
  );
}

function FolderContents({
  api,
  folder,
}: {
  api: ApiClient;
  folder: NamedHierarchyNode;
}) {
  const auth = useAuth();
  const { t, formatTimestamp } = useI18n();
  const [items, setItems] = useState<VisibleContract[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems((await api.listFolderContracts(folder.id)).items);
      setError(null);
    } catch (caught) {
      if (isSignedOutError(caught)) {
        auth.reportSignedOut();
        return;
      }
      setError(describeError(caught, t));
    } finally {
      setLoading(false);
    }
  }, [api, auth, folder.id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <h2 className="ds-text --large">{folder.name}</h2>

      {error !== null && <Alert tone="failed">{error}</Alert>}

      {loading && <p className="ds-text --muted">{t("common.loading")}…</p>}

      {!loading && error === null && items.length === 0 && (
        <p className="ds-text --muted">
          {t("organisation.folders.noContracts")}
        </p>
      )}

      {items.length > 0 && (
        <ul className="app-list">
          {items.map((contract) => (
            <li
              key={contract.contractId}
              className="ds-cols --middle --nowrap --gap-small"
            >
              <Link
                to={`/contracts/${contract.contractId}`}
                className="app-truncate app-grow"
              >
                {contract.extracted?.title ?? contract.originalFileName}
              </Link>
              <StatusBadge status={contract.ingestStatus} />
              <span className="ds-text --small --muted app-mono">
                {formatTimestamp(contract.uploadedAt)}
              </span>
            </li>
          ))}
        </ul>
      )}

      <PermissionEditor
        api={api}
        resourceType="FOLDER"
        resourceId={folder.id}
      />
    </>
  );
}
